// Version 1 — Automation Impact view.
//
// Sibling tab to the staff-plan cards. Computes hours + FTE released by each
// automation in the model's `automationImpacts` catalog, projected across
// Y1 / Y2 / Y3 with a configurable deployment start + ramp duration so the
// view reflects realistic adoption (automation cannot impact instantly).
//
// The math itself lives in the engine (`automationTotals`,
// `automationCellImpacts`, `automationAdoption`) so this tab, the Summary dollar
// tiles and the post-automation matrix cannot drift apart. This file owns the
// presentation: scope chips, filters, deployment controls and the roles
// breakdown.

import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  AlertTriangle,
  Calendar,
  Check,
  ChevronDown,
  ChevronRight,
  Filter,
  Gauge,
  ListTree,
  Save,
  Sparkles,
  Users,
  X,
  Zap,
} from 'lucide-react';
import {
  automationAdoption,
  automationCellImpacts,
  automationTotals,
  FTE_CAPACITY,
  OMCL_PHASES,
  OMCL_SUB_PHASES,
  resolveAutomationDeployment,
} from '@rc/engine';
import type {
  AutomationCellImpact,
  AutomationTotals,
} from '@rc/engine';
import type {
  AutomationBasis,
  AutomationDeploymentSetting,
  AutomationImpactRow,
  CalculationResult,
  ClientProfile,
  DisciplineChannelCell,
  LevelId,
  Model,
  StaffPlan,
} from '@rc/engine/types';
import {
  availableBases,
  basisCells as cellsForBasis,
  basisCoverage,
  resolveBasis,
  type BasisOption,
} from './automationBasis';
import { fmtFte, fmtInt } from '../../lib/format';

interface Props {
  model: Model;
  result: CalculationResult;
  profile: ClientProfile;
  /** The Actual staff plan attached to the scenario, when there is one. Offered
   *  as an alternative basis: automation release can be projected onto the team
   *  a client really has, not just the one the model sizes. */
  staffPlan?: StaffPlan | null;
  /** When provided, deployment-setting edits persist to scenario.profile via
   *  `automationDeployments`. When omitted the row controls are disabled. */
  onProfileChange?: (patch: Partial<ClientProfile>) => void;
}

/** Per-automation deployment settings — resolved (no undefineds). */
interface DeploymentSetting {
  startMonth: number;
  rampMonths: number;
  enabled: boolean;
}

const QUARTER_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: 'Q1 Y1' },
  { value: 3, label: 'Q2 Y1' },
  { value: 6, label: 'Q3 Y1' },
  { value: 9, label: 'Q4 Y1' },
  { value: 12, label: 'Q1 Y2' },
  { value: 15, label: 'Q2 Y2' },
  { value: 18, label: 'Q3 Y2' },
  { value: 21, label: 'Q4 Y2' },
  { value: 24, label: 'Q1 Y3' },
  { value: 27, label: 'Q2 Y3' },
  { value: 30, label: 'Q3 Y3' },
  { value: 33, label: 'Q4 Y3' },
];

const RAMP_OPTIONS: { value: number; label: string; sublabel: string }[] = [
  { value: 3, label: '3 mo', sublabel: 'Plug & play' },
  { value: 6, label: '6 mo', sublabel: 'Fast' },
  { value: 12, label: '12 mo', sublabel: 'Standard' },
  { value: 18, label: '18 mo', sublabel: 'Phased' },
  { value: 24, label: '24 mo', sublabel: 'Slow rollout' },
];

/** Per-cell breakdown of one automation's full-impact hours/FTE, so the
 *  Roles-Impacted card can list which roles release time when the automation
 *  deploys. Σ over all cells reconciles with `automationTotals` by
 *  construction — both aggregate the same per-cell expression. */
type RoleImpactCell = AutomationCellImpact;

/** What grouping actually reads — narrower than a full impact so the rollup can
 *  be exercised without fabricating provenance fields. */
type GroupableRole = Pick<
  AutomationCellImpact,
  | 'disciplineId'
  | 'disciplineLabel'
  | 'channelId'
  | 'channelLabel'
  | 'levelId'
  | 'hoursAtFull'
  | 'fteAtFull'
>;

export interface DisciplineGroup {
  disciplineId: string;
  disciplineLabel: string;
  fteAtFull: number;
  hoursAtFull: number;
  channels: {
    channelId: string;
    channelLabel: string;
    fteAtFull: number;
    levels: { levelId: LevelId; fteAtFull: number; hoursAtFull: number }[];
  }[];
}

/** Roll the flat Discipline × Channel × Level list up into one entry per
 *  discipline, so the expanded card renders a line per channel with its levels
 *  as chips instead of a row per cell. Input order (discipline, channel, level)
 *  is preserved. */
export function groupRolesByDiscipline(
  roles: GroupableRole[],
): DisciplineGroup[] {
  const groups: DisciplineGroup[] = [];
  const byDiscipline = new Map<string, DisciplineGroup>();

  for (const r of roles) {
    let g = byDiscipline.get(r.disciplineId);
    if (!g) {
      g = {
        disciplineId: r.disciplineId,
        disciplineLabel: r.disciplineLabel,
        fteAtFull: 0,
        hoursAtFull: 0,
        channels: [],
      };
      byDiscipline.set(r.disciplineId, g);
      groups.push(g);
    }
    g.fteAtFull += r.fteAtFull;
    g.hoursAtFull += r.hoursAtFull;

    let ch = g.channels.find((c) => c.channelId === r.channelId);
    if (!ch) {
      ch = {
        channelId: r.channelId,
        channelLabel: r.channelLabel,
        fteAtFull: 0,
        levels: [],
      };
      g.channels.push(ch);
    }
    ch.fteAtFull += r.fteAtFull;

    const lv = ch.levels.find((l) => l.levelId === r.levelId);
    if (lv) {
      lv.fteAtFull += r.fteAtFull;
      lv.hoursAtFull += r.hoursAtFull;
    } else {
      ch.levels.push({
        levelId: r.levelId,
        fteAtFull: r.fteAtFull,
        hoursAtFull: r.hoursAtFull,
      });
    }
  }

  return groups;
}

// ---------------------------------------------------------------------------
// Scope taxonomy — turns an automation's `disciplineChannelKeys` and
// `subPhaseIds` into readable chips, and supplies the option lists for the
// library filters. Mirrors buildDcOptions in AutomationImpactsEditor so the
// results view names scope exactly the way the model editor does.
// ---------------------------------------------------------------------------

const DC_SEP = '::';

interface ScopeOption {
  id: string;
  label: string;
}

interface DcPair {
  key: string;
  disciplineId: string;
  channelId: string;
  disciplineLabel: string;
  channelLabel: string | null;
  label: string;
}

/** A discipline plus the channels it runs, as one nested filter group. Channel
 *  option ids are full Discipline × Channel keys so selecting one filters on
 *  that pair rather than on the channel across every discipline. */
interface DisciplineScopeGroup {
  id: string;
  label: string;
  channels: ScopeOption[];
}

interface ScopeTaxonomy {
  dcByKey: Map<string, DcPair>;
  disciplineGroups: DisciplineScopeGroup[];
  subPhases: (ScopeOption & { phaseId: string })[];
  subPhaseById: Map<string, ScopeOption>;
}

function buildScopeTaxonomy(model: Model): ScopeTaxonomy {
  const disciplineLabel = (id: string) =>
    model.disciplines?.find((d) => d.id === id)?.label || id;
  const channelLabelOf = (id: string) =>
    id === 'all' ? null : model.channels?.find((c) => c.id === id)?.label || id;

  // spendPerFte is the canonical Discipline × Channel taxonomy; fall back to
  // the discipline list when it is empty.
  const dcByKey = new Map<string, DcPair>();
  const rows = model.spendPerFte || [];
  const seed: { disciplineId: string; channelId: string }[] = rows.length
    ? rows.map((r) => ({ disciplineId: r.disciplineId, channelId: r.channelId }))
    : (model.disciplines || []).map((d) => ({
        disciplineId: d.id,
        channelId: 'all',
      }));

  for (const { disciplineId, channelId } of seed) {
    const key = `${disciplineId}${DC_SEP}${channelId}`;
    if (dcByKey.has(key)) continue;
    const dl = disciplineLabel(disciplineId);
    const cl = channelLabelOf(channelId);
    dcByKey.set(key, {
      key,
      disciplineId,
      channelId,
      disciplineLabel: dl,
      channelLabel: cl,
      label: cl ? `${dl} · ${cl}` : dl,
    });
  }

  const disciplineGroups: DisciplineScopeGroup[] = [];
  const groupById = new Map<string, DisciplineScopeGroup>();
  for (const p of dcByKey.values()) {
    let g = groupById.get(p.disciplineId);
    if (!g) {
      g = { id: p.disciplineId, label: p.disciplineLabel, channels: [] };
      groupById.set(p.disciplineId, g);
      disciplineGroups.push(g);
    }
    // The `all` pseudo-channel marks channel-agnostic disciplines. It is not a
    // real channel, and the discipline row above already covers it, so it gets
    // no child row of its own.
    if (p.channelId !== 'all') {
      g.channels.push({ id: p.key, label: p.channelLabel || p.channelId });
    }
  }

  const subPhases = OMCL_SUB_PHASES.map((s) => ({
    id: s.id,
    label: s.label,
    phaseId: s.phaseId,
  }));

  return {
    dcByKey,
    disciplineGroups,
    subPhases,
    subPhaseById: new Map(subPhases.map((s) => [s.id, s])),
  };
}

/** Resolve an automation's scope keys into chip-ready labels. A key missing
 *  from the taxonomy still renders, using the raw ids, so stale model data is
 *  visible rather than silently dropped. */
function scopeChipsFor(
  automation: AutomationImpactRow,
  tax: ScopeTaxonomy,
): { dc: string[]; tasks: string[] } {
  const dc = (automation.disciplineChannelKeys || []).map((k) => {
    const pair = tax.dcByKey.get(k);
    if (pair) return pair.label;
    const [d, c] = k.split(DC_SEP);
    return c && c !== 'all' ? `${d} · ${c}` : d || k;
  });
  const tasks = (automation.subPhaseIds || []).map(
    (id) => tax.subPhaseById.get(id)?.label || id,
  );
  return { dc, tasks };
}

export interface ScopeFilter {
  /** Discipline ids and/or `discipline::channel` keys. A bare discipline id
   *  stands for every channel under it, so the two never need to coexist. */
  scope: Set<string>;
  subPhases: Set<string>;
}

const EMPTY_FILTER: ScopeFilter = {
  scope: new Set(),
  subPhases: new Set(),
};

function filterIsActive(f: ScopeFilter): boolean {
  return f.scope.size > 0 || f.subPhases.size > 0;
}

/** An automation matches when it satisfies every axis that has a selection.
 *  An empty scope on an axis means "applies to all" in the model, so such an
 *  automation matches any selection on that axis — as does a channel-agnostic
 *  `all` channel pair, which covers every channel in its discipline. */
export function automationMatchesFilter(
  automation: AutomationImpactRow,
  f: ScopeFilter,
): boolean {
  const keys = automation.disciplineChannelKeys || [];

  if (f.scope.size > 0 && keys.length > 0) {
    const hit = keys.some((k) => {
      if (f.scope.has(k)) return true;
      const [d, ch] = k.split(DC_SEP);
      const discipline = d || '';
      if (f.scope.has(discipline)) return true;
      if (ch === 'all') {
        const prefix = `${discipline}${DC_SEP}`;
        for (const token of f.scope) if (token.startsWith(prefix)) return true;
      }
      return false;
    });
    if (!hit) return false;
  }

  const subs = automation.subPhaseIds || [];
  if (f.subPhases.size > 0 && subs.length > 0) {
    const hit = subs.some((s) => f.subPhases.has(s));
    if (!hit) return false;
  }

  return true;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AutomationImpactView({
  model,
  result,
  profile,
  staffPlan,
  onProfileChange,
}: Props) {
  const automations = useMemo(
    () => (model.automationImpacts || []).filter(Boolean),
    [model.automationImpacts],
  );

  // Resolve (start/ramp/enabled) for each automation by layering scenario
  // overrides on top of model defaults.
  const settings = useMemo(() => {
    const out: Record<string, DeploymentSetting> = {};
    const overrides = profile.automationDeployments || {};
    for (const a of automations) {
      out[a.id] = resolveAutomationDeployment(a, overrides[a.id]);
    }
    return out;
  }, [automations, profile.automationDeployments]);

  const updateSetting = (id: string, patch: Partial<DeploymentSetting>) => {
    if (!onProfileChange) return;
    const cur = profile.automationDeployments?.[id] || {};
    const next: AutomationDeploymentSetting = { ...cur };
    if (typeof patch.startMonth === 'number') next.startMonth = patch.startMonth;
    if (typeof patch.rampMonths === 'number') next.rampMonths = patch.rampMonths;
    if (typeof patch.enabled === 'boolean') next.enabled = patch.enabled;
    onProfileChange({
      automationDeployments: {
        ...(profile.automationDeployments || {}),
        [id]: next,
      },
    });
  };

  // Which roster the reduction percentages apply to. Cost savings are anchored
  // on the Actual plan elsewhere; this only decides whose hours get released.
  const basisOptions = useMemo(
    () => availableBases(profile, staffPlan),
    [profile, staffPlan],
  );
  const basis: AutomationBasis = resolveBasis(profile, basisOptions);

  const cells = useMemo(
    () => cellsForBasis(basis, model, result, profile, staffPlan),
    [basis, model, result, profile, staffPlan],
  );

  const rows = useMemo(() => {
    return automations.map((a) => {
      const setting = settings[a.id] || resolveAutomationDeployment(a);
      const full = automationTotals(model, cells, a);
      const adoptions: [number, number, number] = [
        automationAdoption(1, setting),
        automationAdoption(2, setting),
        automationAdoption(3, setting),
      ];
      const yearImpact = setting.enabled
        ? adoptions.map((adoption) => ({
            adoption,
            hours: full.hoursAtFull * adoption,
            fte: full.fteAtFull * adoption,
          }))
        : adoptions.map(() => ({ adoption: 0, hours: 0, fte: 0 }));
      return {
        automation: a,
        setting,
        full,
        years: yearImpact as [
          { adoption: number; hours: number; fte: number },
          { adoption: number; hours: number; fte: number },
          { adoption: number; hours: number; fte: number },
        ],
      };
    });
  }, [automations, model, cells, settings]);

  // Headline tiles always reflect the whole scenario — the library filter is a
  // browsing aid and must not change the scenario's totals.
  const totals = useMemo(() => {
    let yr1 = 0;
    let yr2 = 0;
    let yr3 = 0;
    let full = 0;
    for (const r of rows) {
      yr1 += r.years[0].fte;
      yr2 += r.years[1].fte;
      yr3 += r.years[2].fte;
      if (r.setting.enabled) full += r.full.fteAtFull;
    }
    return { yr1, yr2, yr3, full };
  }, [rows]);

  const coverage = useMemo(() => basisCoverage(model, cells), [model, cells]);

  const taxonomy = useMemo(() => buildScopeTaxonomy(model), [model]);
  const [filter, setFilter] = useState<ScopeFilter>(EMPTY_FILTER);
  const isFiltered = filterIsActive(filter);

  const visibleRows = useMemo(
    () =>
      isFiltered
        ? rows.filter((r) => automationMatchesFilter(r.automation, filter))
        : rows,
    [rows, filter, isFiltered],
  );

  // Totals for the table footer. Mirrors `totals` but over the visible subset so
  // the footer never contradicts the rows above it.
  const visibleTotals = useMemo(() => {
    let yr1 = 0;
    let yr2 = 0;
    let yr3 = 0;
    let full = 0;
    for (const r of visibleRows) {
      yr1 += r.years[0].fte;
      yr2 += r.years[1].fte;
      yr3 += r.years[2].fte;
      if (r.setting.enabled) full += r.full.fteAtFull;
    }
    return { yr1, yr2, yr3, full };
  }, [visibleRows]);

  if (automations.length === 0) {
    return (
      <section className="card">
        <div className="card-body text-sm text-slate-500">
          No automations configured in the model. Add entries on the{' '}
          <span className="font-medium text-slate-700">Models → Automation impacts</span>{' '}
          tab to see projected hour + FTE savings here.
        </div>
      </section>
    );
  }

  return (
    <div className="space-y-4">
      <SummaryHeader totals={totals} activeCount={rows.filter((r) => r.setting.enabled).length} />

      <section className="card">
        <div className="card-header">
          <div>
            <h3 className="text-base font-semibold flex items-center gap-2">
              <Sparkles size={16} className="text-brand-purple-500" />
              Automation impact by deployment plan
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Each automation's full-impact hours/FTE released, ramped over a 36-month horizon.
              Adjust the deployment start and ramp duration to model phased rollouts.
            </p>
          </div>
          {onProfileChange ? (
            <div className="flex items-center gap-1.5 text-[11px] text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-full px-2.5 py-1">
              <Save size={11} />
              Saves to scenario
            </div>
          ) : (
            <div className="text-[11px] text-slate-500 bg-slate-50 ring-1 ring-slate-200 rounded-full px-2.5 py-1">
              Read-only preview
            </div>
          )}
        </div>
        <div className="card-body p-0">
          <BasisBar
            options={basisOptions}
            value={basis}
            coverage={coverage}
            readOnly={!onProfileChange}
            onChange={(next) => onProfileChange?.({ automationBasis: next })}
          />
          <ScopeFilterBar
            taxonomy={taxonomy}
            filter={filter}
            onChange={setFilter}
            shown={visibleRows.length}
            total={rows.length}
          />
          <div className="overflow-x-auto">
            <table className="w-full text-xs tabular-nums">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left py-2 pl-4 pr-3 font-medium w-10">On</th>
                  <th className="text-left py-2 pr-3 font-medium">Automation</th>
                  <th className="text-right py-2 px-2 font-medium">% saved</th>
                  <th className="text-left py-2 px-2 font-medium">
                    <span className="inline-flex items-center gap-1">
                      <Calendar size={11} />
                      Start
                    </span>
                  </th>
                  <th className="text-left py-2 px-2 font-medium">
                    <span className="inline-flex items-center gap-1">
                      <Gauge size={11} />
                      Ramp
                    </span>
                  </th>
                  <th className="text-center py-2 px-2 font-medium">Year 1</th>
                  <th className="text-center py-2 px-2 font-medium">Year 2</th>
                  <th className="text-center py-2 px-2 font-medium">Year 3</th>
                  <th className="text-center py-2 px-2 font-medium border-l border-slate-200 bg-slate-50/40">
                    At full
                  </th>
                  <th className="text-left py-2 pl-3 pr-4 font-medium w-[280px] border-l border-slate-200">
                    Applies to
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-4 py-8 text-center">
                      <div className="text-sm text-slate-500">
                        No automations match the current filters.
                      </div>
                      <button
                        type="button"
                        onClick={() => setFilter(EMPTY_FILTER)}
                        className="mt-1 text-xs text-brand-purple-600 hover:text-brand-purple-800 underline"
                      >
                        Clear filters
                      </button>
                    </td>
                  </tr>
                )}
                {visibleRows.map((r, i) => (
                  <AutomationRow
                    key={r.automation.id}
                    rowData={r}
                    taxonomy={taxonomy}
                    onSettingChange={(patch) => updateSetting(r.automation.id, patch)}
                    readOnly={!onProfileChange}
                    isLast={i === visibleRows.length - 1}
                  />
                ))}
                {visibleRows.length > 0 && (
                  <tr className="bg-brand-purple-50/40 font-semibold">
                    <td className="py-2 pl-4 pr-3 border-t-2 border-slate-300" />
                    <td
                      className="py-2 pr-3 text-brand-purple-800 border-t-2 border-slate-300"
                      colSpan={4}
                    >
                      {isFiltered
                        ? `Filtered total · ${visibleRows.length} of ${rows.length}`
                        : 'Portfolio total'}
                    </td>
                    <YearTotalCell fte={visibleTotals.yr1} />
                    <YearTotalCell fte={visibleTotals.yr2} />
                    <YearTotalCell fte={visibleTotals.yr3} />
                    <td className="py-2 px-2 text-center text-brand-purple-800 border-t-2 border-slate-300 bg-slate-50/40">
                      {fmtFte(visibleTotals.full)} FTE
                    </td>
                    <td className="py-2 pl-3 pr-4 border-t-2 border-l border-slate-300" />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Legend />
        </div>
      </section>

      <RolesImpactedCard rows={visibleRows} model={model} cells={cells} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SummaryHeader({
  totals,
  activeCount,
}: {
  totals: { yr1: number; yr2: number; yr3: number; full: number };
  activeCount: number;
}) {
  return (
    <section className="card">
      <div className="card-body">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <YearTile
            label="Year 1"
            fte={totals.yr1}
            full={totals.full}
            tone="emerald"
          />
          <YearTile
            label="Year 2"
            fte={totals.yr2}
            full={totals.full}
            tone="emerald"
          />
          <YearTile
            label="Year 3"
            fte={totals.yr3}
            full={totals.full}
            tone="emerald"
          />
          <div className="rounded-lg border border-brand-purple-200 bg-brand-purple-50/40 px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-brand-purple-700/70 font-semibold flex items-center gap-1">
              <Zap size={11} /> Full deployment
            </div>
            <div className="mt-1 text-2xl font-bold text-brand-purple-900 leading-tight">
              {fmtFte(totals.full)}
            </div>
            <div className="text-[11px] text-brand-purple-700/80">
              FTE released · {fmtInt(totals.full * FTE_CAPACITY)} hrs/yr
            </div>
            <div className="text-[10px] text-brand-purple-600/70 mt-1">
              Across {activeCount} active automation{activeCount === 1 ? '' : 's'}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function YearTile({
  label,
  fte,
  full,
  tone,
}: {
  label: string;
  fte: number;
  full: number;
  tone: 'emerald' | 'sky';
}) {
  const pct = full > 0 ? (fte / full) * 100 : 0;
  const tones = {
    emerald: {
      border: 'border-emerald-200',
      bg: 'bg-emerald-50/40',
      label: 'text-emerald-700/70',
      value: 'text-emerald-900',
      sub: 'text-emerald-700/80',
      bar: 'bg-emerald-400',
    },
    sky: {
      border: 'border-sky-200',
      bg: 'bg-sky-50/40',
      label: 'text-sky-700/70',
      value: 'text-sky-900',
      sub: 'text-sky-700/80',
      bar: 'bg-sky-400',
    },
  }[tone];
  return (
    <div className={clsx('rounded-lg border px-4 py-3', tones.border, tones.bg)}>
      <div
        className={clsx(
          'text-[10px] uppercase tracking-wider font-semibold',
          tones.label,
        )}
      >
        {label}
      </div>
      <div className={clsx('mt-1 text-2xl font-bold leading-tight', tones.value)}>
        {fmtFte(fte)}
      </div>
      <div className={clsx('text-[11px]', tones.sub)}>
        FTE · {fmtInt(fte * FTE_CAPACITY)} hrs/yr
      </div>
      <div className="mt-2 h-1 w-full rounded-full bg-white/60 overflow-hidden">
        <div
          className={clsx('h-full rounded-full transition-all', tones.bar)}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
      <div className={clsx('text-[10px] mt-0.5', tones.sub)}>
        {pct.toFixed(0)}% of full impact
      </div>
    </div>
  );
}

function YearTotalCell({ fte }: { fte: number }) {
  return (
    <td className="py-2 px-2 text-center border-t-2 border-slate-300">
      <div className="text-brand-purple-800 font-semibold leading-tight">{fmtFte(fte)}</div>
      <div className="text-[10px] text-brand-purple-700/70">
        {fmtInt(fte * FTE_CAPACITY)} hrs
      </div>
    </td>
  );
}

function YearCell({
  fte,
  hours,
  adoption,
  enabled,
  emphasis,
}: {
  fte: number;
  hours: number;
  adoption: number;
  enabled: boolean;
  emphasis?: 'low' | 'normal' | 'high';
}) {
  const dim = !enabled || fte < 0.05;
  return (
    <td
      className={clsx(
        'py-1.5 px-2 text-center border-t border-slate-100 align-top',
        emphasis === 'high' && !dim && 'bg-emerald-50/40',
      )}
    >
      <div
        className={clsx(
          'leading-tight font-medium',
          dim ? 'text-slate-300' : 'text-slate-800',
        )}
      >
        {dim ? '·' : `${fmtFte(fte)} FTE`}
      </div>
      <div
        className={clsx(
          'text-[10px] leading-tight',
          dim ? 'text-slate-300' : 'text-slate-500',
        )}
      >
        {dim ? '' : `${fmtInt(hours)} hrs`}
      </div>
      {!dim && (
        <div className="mt-1 h-1 w-full rounded-full bg-slate-200 overflow-hidden">
          <div
            className="h-full rounded-full bg-emerald-400"
            style={{ width: `${Math.round(adoption * 100)}%` }}
            title={`${Math.round(adoption * 100)}% avg adoption`}
          />
        </div>
      )}
    </td>
  );
}

function AutomationRow({
  rowData,
  taxonomy,
  onSettingChange,
  readOnly,
  isLast,
}: {
  rowData: {
    automation: AutomationImpactRow;
    setting: DeploymentSetting;
    full: AutomationTotals;
    years: [
      { adoption: number; hours: number; fte: number },
      { adoption: number; hours: number; fte: number },
      { adoption: number; hours: number; fte: number },
    ];
  };
  taxonomy: ScopeTaxonomy;
  onSettingChange: (patch: Partial<DeploymentSetting>) => void;
  readOnly: boolean;
  isLast: boolean;
}) {
  const { automation: a, setting, full, years } = rowData;
  const enabled = setting.enabled;
  const dim = !enabled || full.hoursAtFull < 0.5;
  return (
    <tr className={clsx('hover:bg-slate-50/60', isLast && 'border-b border-slate-100')}>
      <td className="py-2 pl-4 pr-3 border-t border-slate-100">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onSettingChange({ enabled: e.target.checked })}
          disabled={readOnly}
          title={enabled ? 'Active' : 'Inactive'}
        />
      </td>
      <td className="py-2 pr-3 border-t border-slate-100 align-top">
        <div
          className={clsx(
            'font-medium text-sm leading-tight',
            dim ? 'text-slate-400' : 'text-slate-800',
          )}
        >
          {a.label}
        </div>
        {a.description && (
          <div className="text-[11px] text-slate-500 leading-snug max-w-md">
            {a.description}
          </div>
        )}
      </td>
      <td
        className={clsx(
          'py-2 px-2 text-right border-t border-slate-100 font-medium',
          dim ? 'text-slate-400' : 'text-slate-700',
        )}
      >
        {Math.round((a.reductionPct || 0) * 100)}%
      </td>
      <td className="py-2 px-2 border-t border-slate-100">
        <select
          className="input py-0.5 px-1.5 text-xs"
          value={setting.startMonth}
          onChange={(e) => onSettingChange({ startMonth: Number(e.target.value) })}
          disabled={!enabled || readOnly}
        >
          {QUARTER_OPTIONS.map((q) => (
            <option key={q.value} value={q.value}>
              {q.label}
            </option>
          ))}
        </select>
      </td>
      <td className="py-2 px-2 border-t border-slate-100">
        <select
          className="input py-0.5 px-1.5 text-xs"
          value={setting.rampMonths}
          onChange={(e) => onSettingChange({ rampMonths: Number(e.target.value) })}
          disabled={!enabled || readOnly}
          title={`Ramp ${setting.rampMonths} months from start to full impact`}
        >
          {RAMP_OPTIONS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label} — {r.sublabel}
            </option>
          ))}
        </select>
      </td>
      <YearCell
        fte={years[0].fte}
        hours={years[0].hours}
        adoption={years[0].adoption}
        enabled={enabled}
      />
      <YearCell
        fte={years[1].fte}
        hours={years[1].hours}
        adoption={years[1].adoption}
        enabled={enabled}
      />
      <YearCell
        fte={years[2].fte}
        hours={years[2].hours}
        adoption={years[2].adoption}
        enabled={enabled}
        emphasis="high"
      />
      <td className="py-1.5 px-2 text-center border-t border-slate-100 align-top bg-slate-50/40">
        <div
          className={clsx(
            'leading-tight font-medium',
            dim ? 'text-slate-400' : 'text-slate-800',
          )}
        >
          {fmtFte(full.fteAtFull)} FTE
        </div>
        <div
          className={clsx(
            'text-[10px] leading-tight',
            dim ? 'text-slate-400' : 'text-slate-500',
          )}
        >
          {fmtInt(full.hoursAtFull)} hrs
        </div>
      </td>
      <td className="py-1.5 pl-3 pr-4 border-t border-l border-slate-100 align-top">
        <ScopeChips automation={a} taxonomy={taxonomy} dim={dim} />
      </td>
    </tr>
  );
}

/** Where an automation applies, as two colour-coded chip rows: sky for the
 *  Discipline × Channel pairs it targets, indigo for the process stages (task
 *  areas) it touches. The colours are spelled out in the table legend rather
 *  than repeated as a text label on every row. An empty scope on either axis
 *  means "all" in the model, shown as one muted chip instead of every value. */
function ScopeChips({
  automation,
  taxonomy,
  dim,
}: {
  automation: AutomationImpactRow;
  taxonomy: ScopeTaxonomy;
  dim: boolean;
}) {
  const { dc, tasks } = useMemo(
    () => scopeChipsFor(automation, taxonomy),
    [automation, taxonomy],
  );

  return (
    <div className={clsx('space-y-1', dim && 'opacity-60')}>
      <div className="flex flex-wrap gap-1">
        {dc.length === 0 ? (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 italic">
            All disciplines &amp; channels
          </span>
        ) : (
          dc.map((label) => (
            <span
              key={label}
              className="text-[10px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-800 ring-1 ring-sky-200"
              title={`Applies to ${label}`}
            >
              {label}
            </span>
          ))
        )}
      </div>
      <div className="flex flex-wrap gap-1">
        {tasks.length === 0 ? (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 italic">
            All task areas
          </span>
        ) : (
          tasks.map((label) => (
            <span
              key={label}
              className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-800 ring-1 ring-indigo-200"
              title={`Task area: ${label}`}
            >
              {label}
            </span>
          ))
        )}
      </div>
    </div>
  );
}

/** Which roster the automation reductions are applied to. Distinct from the
 *  library filter below it: this changes the numbers, the filter only changes
 *  what is listed. A single available basis renders as a label, not a control. */
function BasisBar({
  options,
  value,
  coverage,
  readOnly,
  onChange,
}: {
  options: BasisOption[];
  value: AutomationBasis;
  coverage: { total: number; covered: number; uncovered: number };
  readOnly: boolean;
  onChange: (next: AutomationBasis) => void;
}) {
  const active = options.find((o) => o.id === value) || options[0]!;
  // Worth flagging only when it would visibly move the numbers.
  const showGap =
    coverage.uncovered > 0.05 &&
    coverage.total > 0 &&
    coverage.uncovered / coverage.total > 0.01;

  return (
    <div className="px-4 py-2.5 border-b border-slate-100 flex flex-wrap items-center gap-x-3 gap-y-2">
      <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500">
        <Users size={12} />
        Applied to
      </span>

      {options.length > 1 && !readOnly ? (
        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
          {options.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => onChange(o.id)}
              title={o.sub}
              aria-pressed={o.id === value}
              className={clsx(
                'px-2.5 py-1 text-[11px] whitespace-nowrap transition-colors',
                o.id === value
                  ? 'bg-brand-purple-50 text-brand-purple-800 font-medium'
                  : 'bg-white text-slate-600 hover:bg-slate-50',
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      ) : (
        <span className="text-[11px] text-slate-700 font-medium">
          {active.label}
        </span>
      )}

      <span className="text-[11px] text-slate-500">{active.sub}</span>

      <span className="ml-auto text-[11px] text-slate-500 tabular-nums">
        {fmtFte(coverage.total)} FTE in scope
      </span>

      {showGap && (
        <div className="w-full flex items-start gap-1.5 text-[11px] text-amber-800">
          <AlertTriangle size={12} className="mt-0.5 shrink-0 text-amber-600" />
          <span>
            {fmtFte(coverage.uncovered)} FTE (
            {Math.round((coverage.uncovered / coverage.total) * 100)}%) sits at a
            role the model's time-concentration table does not cover, so no
            automation can release its hours. Those roles contribute nothing to
            the figures below.
          </span>
        </div>
      )}
    </div>
  );
}

/** Library filter toolbar — two independent axes that narrow which automations
 *  are listed. Axes combine with AND, values within an axis with OR. */
function ScopeFilterBar({
  taxonomy,
  filter,
  onChange,
  shown,
  total,
}: {
  taxonomy: ScopeTaxonomy;
  filter: ScopeFilter;
  onChange: (f: ScopeFilter) => void;
  shown: number;
  total: number;
}) {
  const active = filterIsActive(filter);

  const setAxis = (axis: keyof ScopeFilter, next: Set<string>) =>
    onChange({ ...filter, [axis]: next });

  // Sub-phases are grouped under their OMCL phase so the 15 stages stay
  // scannable in the dropdown.
  const taskGroups = useMemo(
    () =>
      OMCL_PHASES.map((p) => ({
        label: p.label,
        options: taxonomy.subPhases.filter((s) => s.phaseId === p.id),
      })).filter((g) => g.options.length > 0),
    [taxonomy.subPhases],
  );

  // Channels hang off their own discipline, so both live in one list: pick a
  // discipline for all of its channels, or drill into individual pairs.
  const scopeGroups = useMemo(
    () =>
      taxonomy.disciplineGroups.map((g) => ({
        parent: { id: g.id, label: g.label },
        options: g.channels,
      })),
    [taxonomy.disciplineGroups],
  );

  return (
    <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/40 flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500">
        <Filter size={12} />
        Filter library
      </span>

      <ScopeFilterDropdown
        label="Disciplines & channels"
        allLabel="All disciplines & channels"
        groups={scopeGroups}
        selected={filter.scope}
        onChange={(next) => setAxis('scope', next)}
        width={280}
      />
      <ScopeFilterDropdown
        label="Task areas"
        allLabel="All task areas"
        groups={taskGroups}
        selected={filter.subPhases}
        onChange={(next) => setAxis('subPhases', next)}
        width={300}
      />

      <span className="text-[11px] text-slate-500 ml-auto tabular-nums">
        {active ? `${shown} of ${total} shown` : `${total} automations`}
      </span>
      {active && (
        <button
          type="button"
          onClick={() => onChange(EMPTY_FILTER)}
          className="text-[11px] text-brand-purple-600 hover:text-brand-purple-800 inline-flex items-center gap-1"
        >
          <X size={11} />
          Clear
        </button>
      )}
    </div>
  );
}

/** A group is either a caption over a flat list (`label`) or a selectable
 *  parent with nested children (`parent`), which is how channels sit under
 *  their discipline. */
interface FilterGroup {
  label?: string | null;
  parent?: ScopeOption;
  options: ScopeOption[];
}

function ScopeFilterDropdown({
  label,
  allLabel,
  groups,
  selected,
  onChange,
  width = 240,
}: {
  label: string;
  allLabel: string;
  groups: FilterGroup[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  width?: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const isEmpty = groups.every((g) => !g.parent && g.options.length === 0);
  const count = selected.size;

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  /** Selecting a parent supersedes its children, so drop them to keep the
   *  selection canonical — otherwise the count double-reports the same scope. */
  const toggleParent = (parentId: string, childIds: string[]) => {
    const next = new Set(selected);
    if (next.has(parentId)) next.delete(parentId);
    else {
      next.add(parentId);
      for (const c of childIds) next.delete(c);
    }
    onChange(next);
  };

  /** Clicking a child while its parent is selected narrows the selection from
   *  the whole discipline down to just that channel. */
  const toggleChild = (parentId: string | undefined, childId: string) => {
    if (parentId && selected.has(parentId)) {
      const next = new Set(selected);
      next.delete(parentId);
      next.add(childId);
      onChange(next);
      return;
    }
    toggle(childId);
  };

  // A parent covers everything beneath it, so picking all means picking the
  // parents where they exist.
  const pickAllIds = () =>
    new Set(
      groups.flatMap((g) =>
        g.parent ? [g.parent.id] : g.options.map((o) => o.id),
      ),
    );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] whitespace-nowrap transition-colors',
          count > 0
            ? 'border-brand-purple-300 bg-brand-purple-50 text-brand-purple-800'
            : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
        )}
        aria-expanded={open}
      >
        {count > 0 ? `${label}: ${count}` : label}
        <ChevronDown size={11} className="opacity-60" />
      </button>
      {open && (
        <div
          className="absolute left-0 mt-1 z-30 bg-white border border-slate-200 rounded-lg shadow-lg max-h-[380px] overflow-hidden flex flex-col"
          style={{ width, maxWidth: 'calc(100vw - 2rem)' }}
        >
          <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between gap-2">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
              {label}
            </span>
            <div className="flex items-center gap-1 text-[11px]">
              <button
                type="button"
                onClick={() => onChange(new Set())}
                className="text-slate-500 hover:text-slate-800"
              >
                All
              </button>
              <span className="text-slate-300">·</span>
              <button
                type="button"
                onClick={() => onChange(pickAllIds())}
                className="text-slate-500 hover:text-slate-800"
              >
                Pick all
              </button>
            </div>
          </div>
          <div className="overflow-y-auto py-1">
            {isEmpty ? (
              <div className="px-3 py-3 text-xs text-slate-400">
                Nothing to filter on in this model.
              </div>
            ) : (
              groups.map((g, gi) => {
                const parentOn = !!g.parent && selected.has(g.parent.id);
                return (
                  <div key={g.parent?.id ?? g.label ?? `g${gi}`}>
                    {g.label && (
                      <div className="px-3 pt-2 pb-0.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                        {g.label}
                      </div>
                    )}
                    {g.parent && (
                      <button
                        type="button"
                        onClick={() =>
                          toggleParent(
                            g.parent!.id,
                            g.options.map((o) => o.id),
                          )
                        }
                        className={clsx(
                          'w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs text-left font-medium hover:bg-slate-50',
                          parentOn ? 'bg-brand-purple-50/60' : 'mt-0.5',
                        )}
                      >
                        <span
                          className={clsx(
                            'truncate',
                            parentOn ? 'text-slate-800' : 'text-slate-700',
                          )}
                        >
                          {g.parent.label}
                        </span>
                        {parentOn && (
                          <Check
                            size={12}
                            className="text-brand-purple-600 shrink-0"
                          />
                        )}
                      </button>
                    )}
                    {g.options.map((o) => {
                      // A selected parent already includes this channel; show it
                      // as along for the ride rather than as an active choice.
                      const on = selected.has(o.id) || parentOn;
                      return (
                        <button
                          key={o.id}
                          type="button"
                          onClick={() => toggleChild(g.parent?.id, o.id)}
                          className={clsx(
                            'w-full flex items-center justify-between gap-2 py-1.5 text-xs text-left hover:bg-slate-50',
                            g.parent ? 'pl-6 pr-3' : 'px-3',
                            on && !parentOn && 'bg-brand-purple-50/60',
                          )}
                        >
                          <span
                            className={clsx(
                              'truncate',
                              parentOn
                                ? 'text-slate-400'
                                : on
                                  ? 'text-slate-800'
                                  : 'text-slate-600',
                            )}
                          >
                            {o.label}
                          </span>
                          {on && (
                            <Check
                              size={12}
                              className={clsx(
                                'shrink-0',
                                parentOn
                                  ? 'text-slate-300'
                                  : 'text-brand-purple-600',
                              )}
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>
          <div className="border-t border-slate-100 px-3 py-2 flex items-center justify-between">
            <span className="text-[10px] text-slate-400">
              {count === 0 ? allLabel : `${count} selected`}
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs text-brand-purple-600 hover:text-brand-purple-800"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Legend() {
  return (
    <div className="px-4 py-3 border-t border-slate-100 flex flex-wrap items-center gap-x-5 gap-y-1 text-[10px] uppercase tracking-wider text-slate-500">
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-3 h-1 rounded-full bg-emerald-400" />
        Bar under each year = avg adoption that year
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-sm bg-sky-50 ring-1 ring-sky-200" />
        Discipline &middot; Channel
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-sm bg-indigo-50 ring-1 ring-indigo-200" />
        Task area
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-sm bg-slate-50 border border-slate-200" />
        Y3 cells highlighted = run-rate impact
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Roles-impacted card — an accordion with one collapsed summary row per
// automation. The row carries the ramped Y1/Y2/Y3 and at-full totals; expanding
// reveals the underlying Discipline × Channel × Level detail grouped by
// discipline, with levels as chips.
//
// The detail shows at-full values only. Every role inside one automation ramps
// by that automation's single adoption factor, so per-role year figures are the
// at-full number times a constant already displayed on the summary row.
// ---------------------------------------------------------------------------

interface RowData {
  automation: AutomationImpactRow;
  setting: DeploymentSetting;
  full: AutomationTotals;
  years: [
    { adoption: number; hours: number; fte: number },
    { adoption: number; hours: number; fte: number },
    { adoption: number; hours: number; fte: number },
  ];
}

function RolesImpactedCard({
  rows,
  model,
  cells,
}: {
  rows: RowData[];
  model: Model;
  cells: DisciplineChannelCell[];
}) {
  // Pre-compute per-cell impacts once per automation. Cheap because the
  // matching logic is linear in (cells × automations).
  const enriched = useMemo(() => {
    return rows.map((r) => ({
      ...r,
      roles: automationCellImpacts(model, cells, [r.automation]),
    }));
  }, [rows, model, cells]);

  // Skip the card entirely when no automation has any impact (cleaner than a
  // multi-empty-state stack).
  const anyImpact = enriched.some(
    (r) => r.setting.enabled && r.roles.length > 0,
  );

  // Everything starts collapsed so the section stays roughly one screen tall.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Only automations with detail to show can expand, so "all expanded" is
  // measured against those rather than the full list.
  const expandableIds = enriched
    .filter((r) => r.roles.length > 0)
    .map((r) => r.automation.id);
  const expandAll = () => setExpandedIds(new Set(expandableIds));
  const collapseAll = () => setExpandedIds(new Set());
  const allExpanded =
    expandableIds.length > 0 &&
    expandableIds.every((id) => expandedIds.has(id));

  return (
    <section className="card">
      <div className="card-header">
        <div>
          <h3 className="text-base font-semibold flex items-center gap-2">
            <ListTree size={16} className="text-brand-purple-500" />
            Roles impacted by each automation
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Ramped Y1–Y3 and at-full release per automation. Expand a row for
            the Discipline × Channel × Level detail behind it.
          </p>
        </div>
        {anyImpact && expandableIds.length > 0 && (
          <button
            type="button"
            onClick={allExpanded ? collapseAll : expandAll}
            className="text-[11px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
            title={
              allExpanded
                ? 'Collapse every automation'
                : 'Expand every automation'
            }
          >
            {allExpanded ? (
              <>
                <ChevronDown size={11} />
                Collapse all
              </>
            ) : (
              <>
                <ChevronRight size={11} />
                Expand all
              </>
            )}
          </button>
        )}
      </div>
      <div className="card-body p-0">
        {!anyImpact && (
          <div className="px-6 py-8 text-sm text-slate-500 text-center">
            No active automations have role-level impact for this scenario yet.
            Enable an automation above and confirm its scope (Discipline ×
            Channel, Levels, Sub-phases) covers cells with non-zero FTE.
          </div>
        )}
        {anyImpact &&
          enriched.map((r, i) => (
            <AutomationRolesRow
              key={r.automation.id}
              data={r}
              isFirst={i === 0}
              expanded={expandedIds.has(r.automation.id)}
              onToggle={() => toggle(r.automation.id)}
            />
          ))}
      </div>
    </section>
  );
}

function AutomationRolesRow({
  data,
  isFirst,
  expanded,
  onToggle,
}: {
  data: RowData & { roles: RoleImpactCell[] };
  isFirst: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { automation, setting, full, years, roles } = data;
  const enabled = setting.enabled;
  const inactive = !enabled;
  const noScope = roles.length === 0;
  const panelId = `roles-panel-${automation.id}`;

  const groups = useMemo(() => groupRolesByDiscipline(roles), [roles]);

  return (
    <div
      className={clsx(
        'border-slate-200',
        !isFirst && 'border-t',
        inactive && 'opacity-60',
      )}
    >
      {/* Summary row — the whole row toggles. Safe as a button because the
          on/off checkbox and start/ramp selects live in the table above. */}
      <button
        type="button"
        onClick={onToggle}
        disabled={noScope}
        aria-expanded={expanded}
        aria-controls={panelId}
        className={clsx(
          'w-full flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 px-4 py-2.5 text-left',
          noScope
            ? 'cursor-default bg-slate-50/40'
            : 'hover:bg-slate-50 bg-slate-50/40',
        )}
        title={
          noScope
            ? 'No matching cells to break down'
            : expanded
              ? 'Hide role detail'
              : 'Show role detail'
        }
      >
        <span className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="text-slate-400">
            {noScope ? (
              <ChevronRight size={14} className="opacity-30" />
            ) : expanded ? (
              <ChevronDown size={14} />
            ) : (
              <ChevronRight size={14} />
            )}
          </span>
          <Sparkles size={13} className="text-brand-purple-500 shrink-0" />
          <span className="font-semibold text-slate-800 text-sm">
            {automation.label}
          </span>
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-semibold">
            {Math.round((automation.reductionPct || 0) * 100)}% saved
          </span>
          {inactive && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 font-semibold">
              Inactive
            </span>
          )}
          {noScope && enabled && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">
              No matching cells
            </span>
          )}
          {!noScope && (
            <span className="text-[10px] text-slate-500 tabular-nums">
              {roles.length} role{roles.length === 1 ? '' : 's'} ·{' '}
              {groups.length} discipline{groups.length === 1 ? '' : 's'}
            </span>
          )}
        </span>
        <span className="flex items-center gap-3 text-[11px] text-slate-600 tabular-nums">
          <span className="inline-flex items-center gap-1">
            <span className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">
              Y1
            </span>
            <span className="font-medium">{fmtFte(years[0].fte)} FTE</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">
              Y2
            </span>
            <span className="font-medium">{fmtFte(years[1].fte)} FTE</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">
              Y3
            </span>
            <span className="font-medium">{fmtFte(years[2].fte)} FTE</span>
          </span>
          <span className="inline-flex items-center gap-1 pl-3 border-l border-slate-200">
            <span className="text-brand-purple-600 uppercase tracking-wider text-[10px] font-semibold">
              At full
            </span>
            <span className="font-semibold text-brand-purple-800">
              {fmtFte(full.fteAtFull)} FTE
            </span>
          </span>
        </span>
      </button>

      {/* Grouped detail — one block per discipline, a line per channel, levels
          as chips. At-full values only; see the note at the top of this
          section for why year columns are omitted here. */}
      {!noScope && expanded && (
        <div
          id={panelId}
          className="px-4 pb-3 pt-2 border-t border-slate-100 bg-white"
        >
          {automation.description && (
            <p className="text-[11px] text-slate-500 mb-2 max-w-2xl">
              {automation.description}
            </p>
          )}
          <div className="space-y-1.5">
            {groups.map((g) => (
              <div
                key={g.disciplineId}
                className="rounded-md border border-slate-200"
              >
                <div className="flex items-baseline justify-between gap-3 px-3 py-1.5 bg-slate-50/60 border-b border-slate-100">
                  <span className="font-medium text-slate-800 text-xs">
                    {g.disciplineLabel}
                  </span>
                  <span className="text-[10px] text-slate-500 tabular-nums whitespace-nowrap">
                    {fmtFte(g.fteAtFull, 2)} FTE · {fmtInt(g.hoursAtFull)} hrs
                  </span>
                </div>
                <div className="divide-y divide-slate-100">
                  {g.channels.map((ch) => (
                    <div
                      key={ch.channelId}
                      className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-1.5"
                    >
                      <span className="text-[11px] text-slate-500 min-w-[110px] shrink-0">
                        {ch.channelLabel}
                      </span>
                      {ch.levels.map((lv) => (
                        <span
                          key={lv.levelId}
                          className="inline-flex items-center gap-1.5 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-700"
                          title={`${g.disciplineLabel} · ${ch.channelLabel} · ${lv.levelId} — ${fmtFte(lv.fteAtFull, 2)} FTE, ${fmtInt(lv.hoursAtFull)} hrs/yr at full deployment`}
                        >
                          <span className="font-semibold">{lv.levelId}</span>
                          <span className="tabular-nums text-slate-700">
                            {fmtFte(lv.fteAtFull, 2)} FTE
                          </span>
                          <span className="tabular-nums text-slate-500">
                            {fmtInt(lv.hoursAtFull)} hrs
                          </span>
                        </span>
                      ))}
                      <span className="ml-auto text-[10px] text-slate-400 tabular-nums whitespace-nowrap">
                        {fmtFte(ch.fteAtFull, 2)} FTE
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-400 mt-2">
            Chips show FTE and hours released per level at full deployment.
            Y1–Y3 scale these by this automation's adoption ramp.
          </p>
        </div>
      )}
    </div>
  );
}